import React from 'react';
import GiftCardPreview from './GiftCardPreview';
import { GiftCardSettings } from '../services/giftCardSettingsService';

// Fixed base size for consistent rendering
const GIFT_CARD_BASE_WIDTH = 600;
const GIFT_CARD_BASE_HEIGHT = 378; // 600 / 1.586 ≈ 378

interface GiftCardWrapperProps {
  settings?: Partial<GiftCardSettings> | null;
  businessLogoUrl?: string | null;
  code?: string;
  recipientName?: string;
  showSampleData?: boolean;
  className?: string;
  maxWidth?: number | string; // Max width for container (can be number in px or string like "100%")
  maxHeight?: number | string; // Max height for container
}

/**
 * Wrapper component that ensures consistent gift card rendering
 * by using a fixed base size and scaling proportionally to fit containers
 */
export default function GiftCardWrapper({
  settings,
  businessLogoUrl,
  code,
  recipientName,
  showSampleData = false,
  className = '',
  maxWidth,
  maxHeight
}: GiftCardWrapperProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);

  React.useEffect(() => {
    const updateScale = () => {
      const container = containerRef.current;
      if (!container) {
        setScale(1);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const availableWidth = containerRect.width;
      const availableHeight = containerRect.height;

      // Calculate scale based on available space
      const widthScale = availableWidth / GIFT_CARD_BASE_WIDTH;
      const heightScale = availableHeight / GIFT_CARD_BASE_HEIGHT;
      
      // Use the smaller scale to ensure it fits, but don't scale up beyond 100%
      const newScale = Math.min(widthScale, heightScale, 1);
      
      setScale(newScale);
    };

    updateScale();

    // Use ResizeObserver for responsive scaling
    const resizeObserver = new ResizeObserver(updateScale);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateScale);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, []);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: maxWidth || '100%',
    height: maxHeight || 'auto',
    minHeight: maxHeight ? undefined : `${GIFT_CARD_BASE_HEIGHT}px`
  };

  const cardStyle: React.CSSProperties = {
    transform: scale < 1 ? `scale(${scale})` : 'none',
    transformOrigin: 'center center',
    width: GIFT_CARD_BASE_WIDTH + 'px',
    height: GIFT_CARD_BASE_HEIGHT + 'px'
  };

  return (
    <div 
      ref={containerRef}
      className={className}
      style={containerStyle}
    >
      <div style={cardStyle}>
        <GiftCardPreview
          settings={settings}
          businessLogoUrl={businessLogoUrl}
          code={code}
          recipientName={recipientName}
          showSampleData={showSampleData}
          baseWidth={GIFT_CARD_BASE_WIDTH}
        />
      </div>
    </div>
  );
}
